import type { AxiosInstance } from "axios";

import type {
  AllocateResponsibleUserRequest,
  EndInvolvementsRequest,
  PersonId,
  PersonInvolvementId,
  PersonWithIdentityWithheld,
  ReplaceResponsibleUserRequest,
  StartInvolvementsRequest,
} from "@thalamos/haven-common";
import { PersonWithIdentityWithheldSchema } from "@thalamos/haven-common";

/**
 * The answer of a route for a Thalamos admin: the view it sent, a refusal with
 * its status, a success whose body is not the view, or no answer at all.
 */
export type AdminPersonAnswer =
  | { kind: "view"; status: number; view: PersonWithIdentityWithheld }
  | { kind: "refused"; status: number }
  | { kind: "malformed"; status: number }
  | { kind: "unanswered" };

/** A change a Thalamos admin makes to one record. */
export type PersonChange<Request> = { personId: PersonId; request: Request };

const answer = (response: {
  status: number;
  data: unknown;
}): AdminPersonAnswer => {
  if (response.status >= 300) {
    return { kind: "refused", status: response.status };
  }

  const parsed = PersonWithIdentityWithheldSchema.safeParse(response.data);

  return parsed.success
    ? { kind: "view", status: response.status, view: parsed.data }
    : { kind: "malformed", status: response.status };
};

export const api = (apiAxios: AxiosInstance) => {
  const post = ({
    url,
    data,
  }: {
    url: string;
    data?: unknown;
  }): Promise<AdminPersonAnswer> =>
    apiAxios
      .request<unknown>({ url, method: "post", data })
      .then(answer, (): AdminPersonAnswer => ({ kind: "unanswered" }));

  return {
    adminPerson: {
      /** Reads a record by its id, which records the read. */
      readPerson: (personId: PersonId) =>
        post({ url: `/api/admin/persons/${personId}/reads` }),
      start: ({ personId, request }: PersonChange<StartInvolvementsRequest>) =>
        post({
          url: `/api/admin/persons/${personId}/involvements/start`,
          data: request,
        }),
      allocateResponsibleUser: ({
        personId,
        request,
      }: PersonChange<AllocateResponsibleUserRequest>) =>
        post({
          url: `/api/admin/persons/${personId}/allocate-responsible-user`,
          data: request,
        }),
      replaceResponsibleUser: ({
        personId,
        request,
      }: PersonChange<ReplaceResponsibleUserRequest>) =>
        post({
          url: `/api/admin/persons/${personId}/replace-responsible-user`,
          data: request,
        }),
      /** Ends a guest's access to a record at once. */
      endGuestAccess: ({
        personId,
        involvementId,
      }: {
        personId: PersonId;
        involvementId: PersonInvolvementId;
      }) =>
        post({
          url: `/api/admin/persons/${personId}/guests/${involvementId}/end`,
        }),
      end: ({ personId, request }: PersonChange<EndInvolvementsRequest>) =>
        post({
          url: `/api/admin/persons/${personId}/involvements/end`,
          data: request,
        }),
    },
  };
};
